import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "../../context/AuthContext";
import PortalPostsFeed from "../../components/PortalPostsFeed";
import DocumentSignOffDialog from "../../components/DocumentSignOffDialog";
import { useTheme } from "../../context/ThemeContext";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "../../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../../components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import {
  Loader2,
  Calendar,
  DollarSign,
  Clock,
  FileText,
  Wrench,
  CalendarDays,
  Send,
  Settings,
  Sun,
  Moon,
  LogOut,
  User,
  Building2,
  MapPin,
  CheckCircle,
  XCircle,
  AlertCircle,
  Download,
  PenLine,
  CheckCircle2,
  Briefcase,
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EmployeeDashboard = () => {
  const { user, logout } = useAuth();
  const { theme, setTheme } = useTheme();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");

  // Dashboard data
  const [dashboardData, setDashboardData] = useState(null);
  const [schedule, setSchedule] = useState([]);
  const [paySummary, setPaySummary] = useState(null);
  const [safetyDocs, setSafetyDocs] = useState([]);
  const [equipment, setEquipment] = useState([]);
  const [timeOffRequests, setTimeOffRequests] = useState([]);
  const [taxDocuments, setTaxDocuments] = useState({ documents: [], available_years: [] });
  const [selectedTaxDoc, setSelectedTaxDoc] = useState(null);
  const [loadingTaxDoc, setLoadingTaxDoc] = useState(false);

  // Permissions from portal settings
  const [permissions, setPermissions] = useState({
    can_view_schedule: true,
    can_view_pay_summary: true,
    can_submit_time: true,
    can_view_safety_docs: true,
    can_view_equipment: true,
    can_request_time_off: true,
    can_view_tax_docs: true,
    // Phase 19.7
    can_view_pay_stubs: false,
    can_view_own_benefits: false,
    can_view_own_pension: false,
  });

  // Phase 19.7 — Pay stubs / benefits / pension data
  const [payStubs, setPayStubs] = useState([]);
  const [payStubsLoaded, setPayStubsLoaded] = useState(false);
  const [benefitEnrollments, setBenefitEnrollments] = useState([]);
  const [benefitsLoaded, setBenefitsLoaded] = useState(false);
  const [pensionEnrollments, setPensionEnrollments] = useState([]);
  const [pensionLoaded, setPensionLoaded] = useState(false);

  // Phase F.6 Batch D+ — Documents requiring sign-off
  const [jobDocs, setJobDocs] = useState([]);
  const [jobDocAcks, setJobDocAcks] = useState({});
  const [jobDocsLoaded, setJobDocsLoaded] = useState(false);
  const [signOffDoc, setSignOffDoc] = useState(null);
  const [signOffOpen, setSignOffOpen] = useState(false);

  // Dialogs
  const [timeOffDialogOpen, setTimeOffDialogOpen] = useState(false);
  const [timeEntryDialogOpen, setTimeEntryDialogOpen] = useState(false);
  const [settingsDialogOpen, setSettingsDialogOpen] = useState(false);
  const [jobDetailOpen, setJobDetailOpen] = useState(null);

  // Forms
  const [timeOffForm, setTimeOffForm] = useState({
    start_date: "",
    end_date: "",
    reason: "",
    notes: "",
  });
  const [timeEntryForm, setTimeEntryForm] = useState({
    date: new Date().toISOString().split("T")[0],
    hours: "",
    type: "regular",
    notes: "",
  });
  const [saving, setSaving] = useState(false);

  // User preferences
  const [userPreferences, setUserPreferences] = useState({
    accent_color: "#10b981",
    compact_view: false,
  });

  const fetchDashboard = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/employee-portal/dashboard`);
      setDashboardData(response.data);
      if (response.data.permissions) {
        setPermissions(response.data.permissions);
      }
      setEquipment(response.data.equipment || []);
    } catch (error) {
      console.error("Failed to fetch dashboard:", error);
      toast.error("Failed to load dashboard");
    }
  }, []);

  const fetchSchedule = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/employee-portal/schedule`);
      setSchedule(response.data);
    } catch (error) {
      console.error("Failed to fetch schedule:", error);
    }
  }, []);

  const fetchPaySummary = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/employee-portal/pay-summary`);
      setPaySummary(response.data);
    } catch (error) {
      console.error("Failed to fetch pay summary:", error);
    }
  }, []);

  // Phase 19.7 — real pay stubs (from approved/paid payroll runs), benefits, pension
  const fetchPayStubs = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/payroll/me/pay-stubs`);
      setPayStubs(response.data?.pay_stubs || []);
    } catch (error) {
      setPayStubs([]);
    } finally {
      setPayStubsLoaded(true);
    }
  }, []);

  const fetchBenefits = useCallback(async () => {
    try {
      const eid = user?.linked_employee_id;
      const response = await axios.get(
        `${API}/benefits/enrollments${eid ? `?employee_id=${eid}` : ""}`
      );
      setBenefitEnrollments(response.data || []);
    } catch (error) {
      setBenefitEnrollments([]);
    } finally {
      setBenefitsLoaded(true);
    }
  }, [user?.linked_employee_id]);

  const fetchPension = useCallback(async () => {
    try {
      const eid = user?.linked_employee_id;
      const response = await axios.get(
        `${API}/pensions/enrollments${eid ? `?employee_id=${eid}` : ""}`
      );
      setPensionEnrollments(response.data || []);
    } catch (error) {
      setPensionEnrollments([]);
    } finally {
      setPensionLoaded(true);
    }
  }, [user?.linked_employee_id]);

  // Phase F.6 Batch D+ — fetch docs attached to my scheduled jobs + my acks
  const fetchJobDocs = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/employee-portal/documents`);
      setJobDocs(res.data?.documents || []);
      setJobDocAcks(res.data?.my_acknowledgments || {});
    } catch (error) {
      setJobDocs([]);
      setJobDocAcks({});
    } finally {
      setJobDocsLoaded(true);
    }
  }, []);

  const openSignOff = (doc) => {
    setSignOffDoc(doc);
    setSignOffOpen(true);
  };

  const onAckSuccess = () => {
    // Refresh both docs and acks since pending count may update
    fetchJobDocs();
    fetchDashboard();
  };

  const fetchTimeOffRequests = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/employee-portal/time-off-requests`);
      setTimeOffRequests(response.data);
    } catch (error) {
      console.error("Failed to fetch time off requests:", error);
    }
  }, []);

  const fetchTaxDocuments = useCallback(async (year = null) => {
    try {
      const params = year ? `?year=${year}` : '';
      const response = await axios.get(`${API}/employee-portal/tax-documents${params}`);
      setTaxDocuments(response.data);
    } catch (error) {
      console.error("Failed to fetch tax documents:", error);
    }
  }, []);

  const fetchTaxDocumentDetail = async (docType, year) => {
    setLoadingTaxDoc(true);
    try {
      const response = await axios.get(`${API}/employee-portal/tax-documents/${docType}/${year}`);
      setSelectedTaxDoc(response.data);
    } catch (error) {
      console.error("Failed to fetch tax document:", error);
      toast.error("Failed to load tax document");
    } finally {
      setLoadingTaxDoc(false);
    }
  };

  const fetchSafetyDocs = useCallback(async () => {
    try {
      // Get safety documents from the dashboard data
      if (dashboardData?.safety_documents) {
        setSafetyDocs(dashboardData.safety_documents);
      }
    } catch (error) {
      console.error("Failed to fetch safety docs:", error);
    }
  }, [dashboardData]);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([
        fetchDashboard(),
        fetchSchedule(),
        fetchPaySummary(),
        fetchTimeOffRequests(),
        fetchTaxDocuments(),
      ]);
      // Load user preferences from localStorage
      const prefs = localStorage.getItem(`employee_prefs_${user?.id}`);
      if (prefs) {
        setUserPreferences(JSON.parse(prefs));
      }
      setLoading(false);
    };
    loadData();
  }, [fetchDashboard, fetchSchedule, fetchPaySummary, fetchTimeOffRequests, fetchTaxDocuments, user?.id]);

  useEffect(() => {
    if (dashboardData) {
      fetchSafetyDocs();
    }
  }, [dashboardData, fetchSafetyDocs]);

  // Phase 19.7: Lazy-load pay stubs / benefits / pension on tab change
  useEffect(() => {
    if (activeTab === "pay-stubs" && !payStubsLoaded && permissions.can_view_pay_stubs) {
      fetchPayStubs();
    }
    if (activeTab === "benefits" && !benefitsLoaded && permissions.can_view_own_benefits) {
      fetchBenefits();
    }
    if (activeTab === "pension" && !pensionLoaded && permissions.can_view_own_pension) {
      fetchPension();
    }
    if (activeTab === "documents" && !jobDocsLoaded) {
      fetchJobDocs();
    }
  }, [activeTab, payStubsLoaded, benefitsLoaded, pensionLoaded, jobDocsLoaded, permissions, fetchPayStubs, fetchBenefits, fetchPension, fetchJobDocs]);

  // Phase F.6 Batch D+ — Auto-jump to Documents tab when arriving via magic link
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const focusId = params.get("focus");
    if (focusId) {
      setActiveTab("documents");
      setTimeout(() => {
        const el = document.querySelector(`[data-testid="employee-doc-row-${focusId}"]`);
        if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "center" });
          el.classList.add("ring-2", "ring-amber-400");
          setTimeout(() => el.classList.remove("ring-2", "ring-amber-400"), 3500);
        }
      }, 1200);
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, []);

  // Apply accent color
  useEffect(() => {
    document.documentElement.style.setProperty("--accent-custom", userPreferences.accent_color);
  }, [userPreferences.accent_color]);

  const savePreferences = (newPrefs) => {
    setUserPreferences(newPrefs);
    localStorage.setItem(`employee_prefs_${user?.id}`, JSON.stringify(newPrefs));
    toast.success("Preferences saved");
  };

  const handleRequestTimeOff = async (e) => {
    e.preventDefault();
    if (!timeOffForm.start_date || !timeOffForm.end_date || !timeOffForm.reason) {
      toast.error("Please fill all required fields");
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/employee-portal/time-off-request`, null, {
        params: timeOffForm,
      });
      toast.success("Time off request submitted!");
      setTimeOffDialogOpen(false);
      setTimeOffForm({ start_date: "", end_date: "", reason: "", notes: "" });
      fetchTimeOffRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit request");
    } finally {
      setSaving(false);
    }
  };

  const handleSubmitTimeEntry = async (e) => {
    e.preventDefault();
    if (!timeEntryForm.date || !timeEntryForm.hours) {
      toast.error("Please fill all required fields");
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/employee-portal/time-entry`, null, {
        params: timeEntryForm,
      });
      toast.success("Time entry submitted!");
      setTimeEntryDialogOpen(false);
      setTimeEntryForm({ date: new Date().toISOString().split("T")[0], hours: "", type: "regular", notes: "" });
      fetchPaySummary();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit time entry");
    } finally {
      setSaving(false);
    }
  };

  const getStatusBadge = (status) => {
    const variants = {
      pending: "outline",
      approved: "default",
      denied: "destructive",
      scheduled: "default",
      completed: "default",
      in_progress: "secondary",
    };
    const colors = {
      pending: "",
      approved: "bg-emerald-500",
      denied: "bg-red-500",
      scheduled: "bg-blue-500",
      completed: "bg-emerald-500",
      in_progress: "bg-amber-500",
    };
    return (
      <Badge variant={variants[status] || "outline"} className={colors[status]}>
        {status?.replace("_", " ")}
      </Badge>
    );
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
    });
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return "";
    return new Date(dateStr).toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  const branding = dashboardData?.branding || {};
  const employeeInfo = dashboardData?.employee || {};

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-40 border-b bg-card/95 backdrop-blur supports-[backdrop-filter]:bg-card/60">
        <div className="container mx-auto flex h-16 items-center justify-between px-4">
          <div className="flex items-center gap-3">
            {branding.logo_url ? (
              <img src={branding.logo_url} alt="Logo" className="h-8" />
            ) : (
              <div className="w-10 h-10 rounded-lg bg-primary flex items-center justify-center">
                <Building2 className="h-5 w-5 text-primary-foreground" />
              </div>
            )}
            <span className="font-heading text-lg font-semibold">
              {branding.company_name || "Employee Portal"}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
            >
              {theme === "dark" ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
            </Button>
            <Button variant="ghost" size="icon" onClick={() => setSettingsDialogOpen(true)}>
              <Settings className="h-5 w-5" />
            </Button>
            <div className="flex items-center gap-2 ml-2 pl-2 border-l">
              <User className="h-5 w-5 text-muted-foreground" />
              <span className="text-sm font-medium hidden sm:inline">{user?.name}</span>
            </div>
            <Button variant="ghost" size="icon" onClick={logout}>
              <LogOut className="h-5 w-5" />
            </Button>
          </div>
        </div>
      </header>

      <main className="container mx-auto p-4 md:p-8 space-y-6">
        {/* Welcome */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">
              Welcome, {user?.name?.split(" ")[0]}!
            </h1>
            <p className="text-muted-foreground mt-1">
              {employeeInfo.position || "Team Member"} •{" "}
              {employeeInfo.department || "Operations"}
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            {permissions.can_submit_time && (
              <Button onClick={() => setTimeEntryDialogOpen(true)} data-testid="submit-time-btn">
                <Clock className="h-4 w-4 mr-2" />
                Log Time
              </Button>
            )}
            {permissions.can_request_time_off && (
              <Button
                variant="outline"
                onClick={() => setTimeOffDialogOpen(true)}
                data-testid="request-time-off-btn"
              >
                <CalendarDays className="h-4 w-4 mr-2" />
                Request Time Off
              </Button>
            )}
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <Card data-testid="stat-upcoming-jobs">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Upcoming Jobs</p>
                  <p className="text-2xl font-bold">{dashboardData?.stats?.upcoming_jobs || 0}</p>
                </div>
                <Calendar className="h-8 w-8 text-blue-500 opacity-20" />
              </div>
            </CardContent>
          </Card>
          <Card data-testid="stat-hours-this-week">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Hours This Week</p>
                  <p className="text-2xl font-bold">{dashboardData?.stats?.hours_this_week || 0}</p>
                </div>
                <Clock className="h-8 w-8 text-emerald-500 opacity-20" />
              </div>
            </CardContent>
          </Card>
          <Card data-testid="stat-pending-docs">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Pending Docs</p>
                  <p className="text-2xl font-bold">
                    {dashboardData?.stats?.pending_acknowledgments || 0}
                  </p>
                </div>
                <FileText className="h-8 w-8 text-orange-500 opacity-20" />
              </div>
            </CardContent>
          </Card>
          <Card data-testid="stat-equipment">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Assigned Equipment</p>
                  <p className="text-2xl font-bold">{equipment.length}</p>
                </div>
                <Wrench className="h-8 w-8 text-violet-500 opacity-20" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Main Content */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <TabsList className="flex flex-wrap w-full h-auto gap-1 p-1" data-testid="portal-tabs">
            <TabsTrigger value="overview" data-testid="tab-overview" className="flex-1 min-w-[80px]">
              Overview
            </TabsTrigger>
            {permissions.can_view_schedule && (
              <TabsTrigger value="schedule" data-testid="tab-schedule" className="flex-1 min-w-[80px]">
                Schedule
              </TabsTrigger>
            )}
            {permissions.can_view_pay_summary && (
              <TabsTrigger value="pay" data-testid="tab-pay" className="flex-1 min-w-[80px]">
                Pay
              </TabsTrigger>
            )}
            {permissions.can_view_pay_stubs && (
              <TabsTrigger value="pay-stubs" data-testid="tab-pay-stubs" className="flex-1 min-w-[80px]">
                Pay Stubs
              </TabsTrigger>
            )}
            {permissions.can_view_safety_docs && (
              <TabsTrigger value="safety" data-testid="tab-safety" className="flex-1 min-w-[80px]">
                Safety
              </TabsTrigger>
            )}
            {permissions.can_view_equipment && (
              <TabsTrigger value="equipment" data-testid="tab-equipment" className="flex-1 min-w-[80px]">
                Equipment
              </TabsTrigger>
            )}
            {permissions.can_view_own_benefits && (
              <TabsTrigger value="benefits" data-testid="tab-benefits" className="flex-1 min-w-[80px]">
                Benefits
              </TabsTrigger>
            )}
            {permissions.can_view_own_pension && (
              <TabsTrigger value="pension" data-testid="tab-pension" className="flex-1 min-w-[80px]">
                Pension
              </TabsTrigger>
            )}
            {permissions.can_view_tax_docs && (
              <TabsTrigger value="tax-docs" data-testid="tab-tax-docs" className="flex-1 min-w-[80px]">
                Tax Docs
              </TabsTrigger>
            )}
            <TabsTrigger value="documents" data-testid="tab-documents" className="flex-1 min-w-[80px]">
              Documents
            </TabsTrigger>
            <TabsTrigger value="posts" data-testid="tab-posts" className="flex-1 min-w-[80px]">
              Posts
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-4">
            <div className="grid gap-4 md:grid-cols-2">
              {/* Upcoming Schedule */}
              {permissions.can_view_schedule && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Calendar className="h-5 w-5" />
                      Upcoming Jobs
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {schedule.length === 0 ? (
                      <p className="text-center text-muted-foreground py-4">
                        No upcoming jobs scheduled
                      </p>
                    ) : (
                      <div className="space-y-3">
                        {schedule.slice(0, 5).map((job) => (
                          <div
                            key={job.id}
                            className="flex items-center justify-between p-3 border rounded-lg hover:bg-accent/50 cursor-pointer transition-colors"
                            onClick={() => setJobDetailOpen(job)}
                            data-testid={`job-item-${job.id}`}
                          >
                            <div>
                              <p className="font-medium">{job.title || job.company_name}</p>
                              <p className="text-sm text-muted-foreground flex items-center gap-1">
                                <MapPin className="h-3 w-3" />
                                {job.location || "TBD"}
                              </p>
                            </div>
                            <div className="text-right">
                              <p className="text-sm font-medium">{formatDate(job.date)}</p>
                              <p className="text-xs text-muted-foreground">{formatTime(job.start_time)}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Time Off Requests */}
              {permissions.can_request_time_off && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <CalendarDays className="h-5 w-5" />
                      Time Off Requests
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {timeOffRequests.length === 0 ? (
                      <p className="text-center text-muted-foreground py-4">
                        No time off requests
                      </p>
                    ) : (
                      <div className="space-y-3">
                        {timeOffRequests.slice(0, 5).map((request) => (
                          <div
                            key={request.id}
                            className="flex items-center justify-between p-3 border rounded-lg"
                            data-testid={`timeoff-item-${request.id}`}
                          >
                            <div>
                              <p className="font-medium">{request.reason}</p>
                              <p className="text-sm text-muted-foreground">
                                {formatDate(request.start_date)} - {formatDate(request.end_date)}
                              </p>
                            </div>
                            {getStatusBadge(request.status)}
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>

          {/* Schedule Tab */}
          {permissions.can_view_schedule && (
            <TabsContent value="schedule">
              <Card>
                <CardHeader>
                  <CardTitle>My Schedule</CardTitle>
                  <CardDescription>Your upcoming jobs and assignments</CardDescription>
                </CardHeader>
                <CardContent>
                  {schedule.length === 0 ? (
                    <p className="text-center text-muted-foreground py-8">
                      No jobs scheduled
                    </p>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Date</TableHead>
                          <TableHead>Job</TableHead>
                          <TableHead>Location</TableHead>
                          <TableHead>Time</TableHead>
                          <TableHead>Status</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {schedule.map((job) => (
                          <TableRow
                            key={job.id}
                            className="cursor-pointer hover:bg-accent/50"
                            onClick={() => setJobDetailOpen(job)}
                          >
                            <TableCell className="font-medium">{formatDate(job.date)}</TableCell>
                            <TableCell>{job.title || job.company_name}</TableCell>
                            <TableCell>{job.location || "-"}</TableCell>
                            <TableCell>
                              {formatTime(job.start_time)}
                              {job.end_time && ` - ${formatTime(job.end_time)}`}
                            </TableCell>
                            <TableCell>{getStatusBadge(job.status)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          )}

          {/* Pay Tab */}
          {permissions.can_view_pay_summary && (
            <TabsContent value="pay">
              <div className="grid gap-4 md:grid-cols-2">
                <Card>
                  <CardHeader>
                    <CardTitle>Current Period Summary</CardTitle>
                    <CardDescription>Your earnings this pay period</CardDescription>
                  </CardHeader>
                  <CardContent>
                    {paySummary ? (
                      <div className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                          <div className="p-4 border rounded-lg">
                            <p className="text-sm text-muted-foreground">Regular Hours</p>
                            <p className="text-2xl font-bold">{paySummary.regular_hours || 0}</p>
                          </div>
                          <div className="p-4 border rounded-lg">
                            <p className="text-sm text-muted-foreground">Overtime Hours</p>
                            <p className="text-2xl font-bold">{paySummary.overtime_hours || 0}</p>
                          </div>
                        </div>
                        <div className="p-4 border rounded-lg bg-accent/50">
                          <p className="text-sm text-muted-foreground">Gross Pay (Estimated)</p>
                          <p className="text-3xl font-bold">
                            ${paySummary.gross_pay?.toLocaleString() || "0"}
                          </p>
                        </div>
                        {paySummary.deductions && paySummary.deductions.length > 0 && (
                          <div>
                            <p className="text-sm font-medium mb-2">Deductions</p>
                            <div className="space-y-2">
                              {paySummary.deductions.map((d, i) => (
                                <div key={i} className="flex justify-between text-sm">
                                  <span className="text-muted-foreground">{d.name}</span>
                                  <span className="text-red-500">-${d.amount?.toLocaleString()}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        <div className="p-4 border-2 border-primary rounded-lg">
                          <p className="text-sm text-muted-foreground">Net Pay (Estimated)</p>
                          <p className="text-3xl font-bold text-primary">
                            ${paySummary.net_pay?.toLocaleString() || "0"}
                          </p>
                        </div>
                      </div>
                    ) : (
                      <p className="text-center text-muted-foreground py-8">
                        No pay data available
                      </p>
                    )}
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>Year-to-Date</CardTitle>
                    <CardDescription>Your earnings this year</CardDescription>
                  </CardHeader>
                  <CardContent>
                    {paySummary?.ytd ? (
                      <div className="space-y-4">
                        <div className="p-4 border rounded-lg">
                          <p className="text-sm text-muted-foreground">Total Hours</p>
                          <p className="text-2xl font-bold">{paySummary.ytd.total_hours || 0}</p>
                        </div>
                        <div className="p-4 border rounded-lg">
                          <p className="text-sm text-muted-foreground">Gross Earnings</p>
                          <p className="text-2xl font-bold">
                            ${paySummary.ytd.gross_earnings?.toLocaleString() || "0"}
                          </p>
                        </div>
                        <div className="p-4 border rounded-lg">
                          <p className="text-sm text-muted-foreground">Total Deductions</p>
                          <p className="text-2xl font-bold text-red-500">
                            -${paySummary.ytd.total_deductions?.toLocaleString() || "0"}
                          </p>
                        </div>
                        <div className="p-4 border-2 border-primary rounded-lg">
                          <p className="text-sm text-muted-foreground">Net Earnings YTD</p>
                          <p className="text-3xl font-bold text-primary">
                            ${paySummary.ytd.net_earnings?.toLocaleString() || "0"}
                          </p>
                        </div>
                      </div>
                    ) : (
                      <p className="text-center text-muted-foreground py-8">
                        No YTD data available
                      </p>
                    )}
                  </CardContent>
                </Card>
              </div>
            </TabsContent>
          )}

          {/* Safety Documents Tab */}
          {permissions.can_view_safety_docs && (
            <TabsContent value="safety">
              <Card>
                <CardHeader>
                  <CardTitle>Safety Documents</CardTitle>
                  <CardDescription>Required safety documentation</CardDescription>
                </CardHeader>
                <CardContent>
                  {safetyDocs.length === 0 ? (
                    <p className="text-center text-muted-foreground py-8">
                      No safety documents assigned
                    </p>
                  ) : (
                    <div className="space-y-3">
                      {safetyDocs.map((doc) => (
                        <div
                          key={doc.id}
                          className="flex items-center justify-between p-4 border rounded-lg"
                          data-testid={`safety-doc-${doc.id}`}
                        >
                          <div className="flex items-center gap-3">
                            <FileText className="h-8 w-8 text-muted-foreground" />
                            <div>
                              <p className="font-medium">{doc.title}</p>
                              <p className="text-sm text-muted-foreground">
                                Category: {doc.category}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {doc.acknowledged ? (
                              <Badge className="bg-emerald-500">
                                <CheckCircle className="h-3 w-3 mr-1" />
                                Acknowledged
                              </Badge>
                            ) : (
                              <Badge variant="outline" className="text-orange-500 border-orange-500">
                                <AlertCircle className="h-3 w-3 mr-1" />
                                Pending
                              </Badge>
                            )}
                            {doc.file_url && (
                              <Button variant="ghost" size="sm" asChild>
                                <a href={doc.file_url} target="_blank" rel="noopener noreferrer">
                                  <Download className="h-4 w-4" />
                                </a>
                              </Button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          )}

          {/* Equipment Tab */}
          {permissions.can_view_equipment && (
            <TabsContent value="equipment">
              <Card>
                <CardHeader>
                  <CardTitle>Assigned Equipment</CardTitle>
                  <CardDescription>Equipment and tools assigned to you</CardDescription>
                </CardHeader>
                <CardContent>
                  {equipment.length === 0 ? (
                    <p className="text-center text-muted-foreground py-8">
                      No equipment assigned
                    </p>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Equipment</TableHead>
                          <TableHead>Category</TableHead>
                          <TableHead>Serial #</TableHead>
                          <TableHead>Condition</TableHead>
                          <TableHead>Assigned Date</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {equipment.map((item) => (
                          <TableRow key={item.id}>
                            <TableCell className="font-medium">{item.name}</TableCell>
                            <TableCell>{item.category}</TableCell>
                            <TableCell>{item.serial_number || "-"}</TableCell>
                            <TableCell>
                              <Badge
                                variant={
                                  item.condition === "Excellent" || item.condition === "Good"
                                    ? "default"
                                    : item.condition === "Fair"
                                    ? "secondary"
                                    : "destructive"
                                }
                              >
                                {item.condition}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              {item.assigned_date
                                ? new Date(item.assigned_date).toLocaleDateString()
                                : "-"}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          )}

          {/* Phase 19.7 — Pay Stubs Tab (real data from payroll runs) */}
          {permissions.can_view_pay_stubs && (
            <TabsContent value="pay-stubs">
              <Card data-testid="paystubs-card">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <FileText className="h-5 w-5" />
                    My Pay Stubs
                  </CardTitle>
                  <CardDescription>
                    Stubs from approved &amp; paid payroll runs. Newest appear first.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {!payStubsLoaded ? (
                    <div className="flex justify-center py-10">
                      <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                    </div>
                  ) : payStubs.length === 0 ? (
                    <div className="text-center py-10 text-muted-foreground" data-testid="paystubs-empty-portal">
                      <FileText className="h-10 w-10 mx-auto mb-2 opacity-40" />
                      <p className="text-sm">No pay stubs yet</p>
                      <p className="text-xs mt-1">
                        Stubs appear here after your manager approves a payroll run.
                      </p>
                    </div>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Period</TableHead>
                          <TableHead>Pay Date</TableHead>
                          <TableHead className="text-right">Hours</TableHead>
                          <TableHead className="text-right">Gross</TableHead>
                          <TableHead className="text-right">Net</TableHead>
                          <TableHead>Status</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {payStubs.map((s) => (
                          <TableRow key={s.id} data-testid={`portal-paystub-${s.id}`}>
                            <TableCell className="text-sm">{s.period_start} – {s.period_end}</TableCell>
                            <TableCell className="text-sm">{s.pay_date || "—"}</TableCell>
                            <TableCell className="text-right">
                              {(s.regular_hours || 0).toFixed(1)}
                              {s.overtime_hours ? ` (+${s.overtime_hours.toFixed(1)} OT)` : ""}
                            </TableCell>
                            <TableCell className="text-right">${(s.gross_pay || 0).toFixed(2)}</TableCell>
                            <TableCell className="text-right font-semibold text-emerald-700">
                              ${(s.net_pay || 0).toFixed(2)}
                            </TableCell>
                            <TableCell>
                              <Badge variant="outline" className={
                                s.status === "paid"
                                  ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                                  : "bg-blue-50 text-blue-700 border-blue-200"
                              }>
                                {s.status}
                              </Badge>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          )}

          {/* Phase 19.7 — Benefits Tab (employee's own enrollments) */}
          {permissions.can_view_own_benefits && (
            <TabsContent value="benefits">
              <Card data-testid="my-benefits-card">
                <CardHeader>
                  <CardTitle>My Benefits</CardTitle>
                  <CardDescription>
                    Health, dental, vision, life, and disability plans you are enrolled in.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {!benefitsLoaded ? (
                    <div className="flex justify-center py-10">
                      <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                    </div>
                  ) : benefitEnrollments.length === 0 ? (
                    <p className="text-center text-muted-foreground py-10 text-sm" data-testid="benefits-empty">
                      You are not currently enrolled in any benefit plans.
                    </p>
                  ) : (
                    <div className="grid gap-3 md:grid-cols-2">
                      {benefitEnrollments.map((e) => (
                        <div
                          key={e.id}
                          className="p-4 border rounded-lg space-y-1"
                          data-testid={`portal-benefit-${e.id}`}
                        >
                          <div className="flex items-center justify-between">
                            <p className="font-semibold">{e.plan?.name || "Benefit Plan"}</p>
                            <Badge variant="outline" className="capitalize">{e.status}</Badge>
                          </div>
                          <p className="text-sm text-muted-foreground capitalize">
                            {e.plan?.plan_type} {e.plan?.provider ? `· ${e.plan.provider}` : ""}
                          </p>
                          {e.effective_date && (
                            <p className="text-xs text-muted-foreground">
                              Effective {e.effective_date}
                              {e.end_date ? ` – ${e.end_date}` : ""}
                            </p>
                          )}
                          {e.plan?.monthly_cost ? (
                            <p className="text-xs">
                              Monthly cost: ${e.plan.monthly_cost.toFixed(2)} · Employer covers{" "}
                              {e.plan.employer_contribution || 0}%
                            </p>
                          ) : null}
                          {e.dependents > 0 && (
                            <p className="text-xs">Dependents: {e.dependents}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          )}

          {/* Phase 19.7 — Pension Tab (employee's own enrollment) */}
          {permissions.can_view_own_pension && (
            <TabsContent value="pension">
              <Card data-testid="my-pension-card">
                <CardHeader>
                  <CardTitle>My Pension</CardTitle>
                  <CardDescription>
                    Your retirement contributions and employer match.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {!pensionLoaded ? (
                    <div className="flex justify-center py-10">
                      <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                    </div>
                  ) : pensionEnrollments.length === 0 ? (
                    <p className="text-center text-muted-foreground py-10 text-sm" data-testid="pension-empty">
                      You are not currently enrolled in a pension plan.
                    </p>
                  ) : (
                    <div className="grid gap-3 md:grid-cols-2">
                      {pensionEnrollments.map((e) => (
                        <div
                          key={e.id}
                          className="p-4 border rounded-lg space-y-1"
                          data-testid={`portal-pension-${e.id}`}
                        >
                          <div className="flex items-center justify-between">
                            <p className="font-semibold">{e.plan_name || "Pension Plan"}</p>
                            <Badge variant="outline" className="capitalize">{e.status}</Badge>
                          </div>
                          {e.plan_provider && (
                            <p className="text-sm text-muted-foreground">{e.plan_provider}</p>
                          )}
                          <div className="grid grid-cols-2 gap-2 pt-2 text-sm">
                            <div>
                              <p className="text-xs text-muted-foreground">Your Contribution</p>
                              <p className="font-medium">{e.contribution_pct || 0}%</p>
                            </div>
                            <div>
                              <p className="text-xs text-muted-foreground">Employer Match</p>
                              <p className="font-medium">{e.plan_employer_match_pct || 0}%</p>
                            </div>
                          </div>
                          {e.beneficiary && (
                            <p className="text-xs text-muted-foreground pt-2">
                              Beneficiary: {e.beneficiary}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          )}

          {/* Tax Documents Tab */}
          {permissions.can_view_tax_docs && (
            <TabsContent value="tax-docs">
              <div className="grid gap-6 lg:grid-cols-2">
                {/* Available Tax Documents */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <FileText className="h-5 w-5" />
                      Tax Documents
                    </CardTitle>
                    <CardDescription>
                      Your tax forms and year-end documents
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    {/* Year Selector */}
                    <div className="mb-4">
                      <Label className="text-sm text-muted-foreground">Select Year</Label>
                      <Select 
                        value={String(taxDocuments.year || new Date().getFullYear() - 1)}
                        onValueChange={(val) => fetchTaxDocuments(parseInt(val))}
                      >
                        <SelectTrigger className="w-40 mt-1">
                          <SelectValue placeholder="Select year" />
                        </SelectTrigger>
                        <SelectContent>
                          {taxDocuments.available_years?.map((y) => (
                            <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {taxDocuments.documents?.length === 0 ? (
                      <div className="text-center py-8">
                        <FileText className="h-12 w-12 mx-auto text-muted-foreground/30 mb-3" />
                        <p className="text-muted-foreground">No tax documents available for this year</p>
                        <p className="text-sm text-muted-foreground/70 mt-1">
                          Documents are typically available after year-end
                        </p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {taxDocuments.documents?.map((doc) => (
                          <div
                            key={doc.id}
                            className="flex items-center justify-between p-4 border rounded-lg hover:bg-accent/50 cursor-pointer transition-colors"
                            onClick={() => fetchTaxDocumentDetail(doc.type, doc.year)}
                            data-testid={`tax-doc-${doc.type}-${doc.year}`}
                          >
                            <div className="flex items-center gap-3">
                              <div className="p-2 bg-primary/10 rounded-lg">
                                <FileText className="h-5 w-5 text-primary" />
                              </div>
                              <div>
                                <p className="font-medium">{doc.name}</p>
                                <p className="text-sm text-muted-foreground">{doc.description}</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">
                                {doc.status}
                              </Badge>
                              <Download className="h-4 w-4 text-muted-foreground" />
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Tax Document Preview */}
                <Card>
                  <CardHeader>
                    <CardTitle>Document Preview</CardTitle>
                    <CardDescription>
                      {selectedTaxDoc ? `${selectedTaxDoc.type?.toUpperCase()} - ${selectedTaxDoc.year}` : "Select a document to preview"}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    {loadingTaxDoc ? (
                      <div className="flex items-center justify-center py-12">
                        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                      </div>
                    ) : !selectedTaxDoc ? (
                      <div className="text-center py-12 text-muted-foreground">
                        <FileText className="h-12 w-12 mx-auto mb-3 opacity-30" />
                        <p>Click on a document to view details</p>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {/* Employer Info */}
                        <div className="p-3 bg-muted/50 rounded-lg">
                          <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">Employer</p>
                          <p className="font-medium">{selectedTaxDoc.employer?.name}</p>
                          <p className="text-sm text-muted-foreground">{selectedTaxDoc.employer?.address}</p>
                        </div>

                        {/* Employee Info */}
                        <div className="p-3 bg-muted/50 rounded-lg">
                          <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">Employee</p>
                          <p className="font-medium">{selectedTaxDoc.employee?.name}</p>
                          <p className="text-sm text-muted-foreground">SIN: {selectedTaxDoc.employee?.sin_masked}</p>
                        </div>

                        {/* Key Figures */}
                        {selectedTaxDoc.document && (
                          <div className="grid grid-cols-2 gap-3">
                            {selectedTaxDoc.type === "t4" && (
                              <>
                                <div className="p-3 border rounded-lg">
                                  <p className="text-xs text-muted-foreground">Employment Income (Box 14)</p>
                                  <p className="text-lg font-semibold text-emerald-600">
                                    ${selectedTaxDoc.document.employment_income?.toLocaleString()}
                                  </p>
                                </div>
                                <div className="p-3 border rounded-lg">
                                  <p className="text-xs text-muted-foreground">Income Tax Deducted (Box 22)</p>
                                  <p className="text-lg font-semibold">
                                    ${selectedTaxDoc.document.income_tax_deducted?.toLocaleString()}
                                  </p>
                                </div>
                                <div className="p-3 border rounded-lg">
                                  <p className="text-xs text-muted-foreground">CPP Contributions (Box 16)</p>
                                  <p className="text-lg font-semibold">
                                    ${selectedTaxDoc.document.cpp_contributions?.toLocaleString()}
                                  </p>
                                </div>
                                <div className="p-3 border rounded-lg">
                                  <p className="text-xs text-muted-foreground">EI Premiums (Box 18)</p>
                                  <p className="text-lg font-semibold">
                                    ${selectedTaxDoc.document.ei_premiums?.toLocaleString()}
                                  </p>
                                </div>
                              </>
                            )}
                            {selectedTaxDoc.type === "w2" && (
                              <>
                                <div className="p-3 border rounded-lg">
                                  <p className="text-xs text-muted-foreground">Wages (Box 1)</p>
                                  <p className="text-lg font-semibold text-emerald-600">
                                    ${selectedTaxDoc.document.wages_tips?.toLocaleString()}
                                  </p>
                                </div>
                                <div className="p-3 border rounded-lg">
                                  <p className="text-xs text-muted-foreground">Federal Tax (Box 2)</p>
                                  <p className="text-lg font-semibold">
                                    ${selectedTaxDoc.document.federal_income_tax?.toLocaleString()}
                                  </p>
                                </div>
                                <div className="p-3 border rounded-lg">
                                  <p className="text-xs text-muted-foreground">Social Security Tax (Box 4)</p>
                                  <p className="text-lg font-semibold">
                                    ${selectedTaxDoc.document.social_security_tax?.toLocaleString()}
                                  </p>
                                </div>
                                <div className="p-3 border rounded-lg">
                                  <p className="text-xs text-muted-foreground">Medicare Tax (Box 6)</p>
                                  <p className="text-lg font-semibold">
                                    ${selectedTaxDoc.document.medicare_tax?.toLocaleString()}
                                  </p>
                                </div>
                              </>
                            )}
                            {selectedTaxDoc.type === "p60" && (
                              <>
                                <div className="p-3 border rounded-lg">
                                  <p className="text-xs text-muted-foreground">Total Pay</p>
                                  <p className="text-lg font-semibold text-emerald-600">
                                    £{selectedTaxDoc.document.total_pay?.toLocaleString()}
                                  </p>
                                </div>
                                <div className="p-3 border rounded-lg">
                                  <p className="text-xs text-muted-foreground">Total Tax</p>
                                  <p className="text-lg font-semibold">
                                    £{selectedTaxDoc.document.total_tax?.toLocaleString()}
                                  </p>
                                </div>
                                <div className="p-3 border rounded-lg col-span-2">
                                  <p className="text-xs text-muted-foreground">National Insurance</p>
                                  <p className="text-lg font-semibold">
                                    £{selectedTaxDoc.document.national_insurance?.toLocaleString()}
                                  </p>
                                </div>
                              </>
                            )}
                          </div>
                        )}

                        <Button className="w-full" variant="outline">
                          <Download className="h-4 w-4 mr-2" />
                          Download PDF
                        </Button>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            </TabsContent>
          )}

          {/* Phase F.6 Batch D+ — Documents tied to my scheduled jobs */}
          <TabsContent value="documents" className="space-y-4">
            <Card data-testid="employee-docs-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="h-5 w-5" />
                  Job Documents
                </CardTitle>
                <CardDescription>
                  Contracts, work orders, NDAs, and safety paperwork attached to jobs you&apos;re assigned to.
                </CardDescription>
              </CardHeader>
              <CardContent>
                {!jobDocsLoaded ? (
                  <div className="flex justify-center py-10">
                    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                  </div>
                ) : jobDocs.length === 0 ? (
                  <div className="text-center py-10 text-muted-foreground" data-testid="employee-docs-empty">
                    <FileText className="h-10 w-10 mx-auto mb-2 opacity-40" />
                    <p className="text-sm">No documents attached to your jobs yet.</p>
                    <p className="text-xs mt-1">
                      Your dispatcher can attach paperwork to a scheduled job and it&apos;ll show up here.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {jobDocs.map((doc) => {
                      const docAcks = jobDocAcks[doc.id] || [];
                      const hasRequiredAck = doc.requires_action
                        ? docAcks.some((a) => a.action === doc.requires_action)
                        : false;
                      const actionLabel = {
                        read: "Mark Read",
                        signed: "Sign & Submit",
                        approved: "Approve",
                        completed: "Mark Completed",
                        rejected: "Reject",
                      }[doc.requires_action] || "Acknowledge";
                      return (
                        <div
                          key={doc.id}
                          className="flex items-center justify-between p-3 border rounded-lg hover:bg-accent/30 transition-colors"
                          data-testid={`employee-doc-row-${doc.id}`}
                        >
                          <div className="min-w-0 flex-1">
                            <div className="font-medium text-sm truncate flex flex-wrap items-center gap-2">
                              <span>{doc.file_name}</span>
                              {doc.requires_action && !hasRequiredAck && (
                                <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-300" data-testid={`emp-doc-action-required-${doc.id}`}>
                                  Action required
                                </Badge>
                              )}
                              {doc.requires_action && hasRequiredAck && (
                                <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-300" data-testid={`emp-doc-ack-done-${doc.id}`}>
                                  <CheckCircle2 className="h-3 w-3 mr-1" />
                                  {doc.requires_action}
                                </Badge>
                              )}
                            </div>
                            <p className="text-xs text-muted-foreground">
                              {new Date(doc.created_at).toLocaleDateString()}
                              {doc.note ? ` • ${doc.note}` : ""}
                            </p>
                          </div>
                          <div className="flex items-center gap-2">
                            {doc.requires_action && !hasRequiredAck && (
                              <Button
                                size="sm"
                                onClick={() => openSignOff(doc)}
                                data-testid={`employee-doc-action-${doc.id}`}
                              >
                                <PenLine className="h-3.5 w-3.5 mr-1" />
                                {actionLabel}
                              </Button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Phase F.3 — Posts tab (employee-audience notice board) */}
          <TabsContent value="posts" className="space-y-4">
            <PortalPostsFeed />
          </TabsContent>
        </Tabs>
      </main>

      {/* Phase F.6 Batch D+ — Document Sign-off Dialog */}
      <DocumentSignOffDialog
        open={signOffOpen}
        onOpenChange={(o) => { setSignOffOpen(o); if (!o) setSignOffDoc(null); }}
        doc={signOffDoc}
        currentUserName={user?.name || ""}
        onSuccess={onAckSuccess}
      />

      {/* Job Detail Dialog */}
      <Dialog open={!!jobDetailOpen} onOpenChange={() => setJobDetailOpen(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Job Details</DialogTitle>
            <DialogDescription>
              {formatDate(jobDetailOpen?.date)}
            </DialogDescription>
          </DialogHeader>
          {jobDetailOpen && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">Client</p>
                  <p className="font-medium">{jobDetailOpen.company_name || "-"}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Status</p>
                  <div>{getStatusBadge(jobDetailOpen.status)}</div>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Time</p>
                  <p className="font-medium">
                    {formatTime(jobDetailOpen.start_time)}
                    {jobDetailOpen.end_time && ` - ${formatTime(jobDetailOpen.end_time)}`}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Estimated Hours</p>
                  <p className="font-medium">{jobDetailOpen.estimated_hours || "-"}</p>
                </div>
              </div>

              <div>
                <p className="text-sm text-muted-foreground">Location</p>
                <p className="font-medium flex items-center gap-1">
                  <MapPin className="h-4 w-4" />
                  {jobDetailOpen.location || "To be determined"}
                </p>
              </div>

              {jobDetailOpen.notes && (
                <div>
                  <p className="text-sm text-muted-foreground">Notes</p>
                  <p className="text-sm">{jobDetailOpen.notes}</p>
                </div>
              )}

              {jobDetailOpen.safety_documents && jobDetailOpen.safety_documents.length > 0 && (
                <div>
                  <p className="text-sm text-muted-foreground mb-2">Required Safety Documents</p>
                  <div className="space-y-2">
                    {jobDetailOpen.safety_documents.map((doc, idx) => (
                      <div
                        key={idx}
                        className="flex items-center justify-between p-2 border rounded"
                      >
                        <span className="text-sm">{doc.title}</span>
                        {doc.acknowledged ? (
                          <CheckCircle className="h-4 w-4 text-emerald-500" />
                        ) : (
                          <AlertCircle className="h-4 w-4 text-orange-500" />
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Time Off Request Dialog */}
      <Dialog open={timeOffDialogOpen} onOpenChange={setTimeOffDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Request Time Off</DialogTitle>
            <DialogDescription>Submit a time off request for approval</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleRequestTimeOff} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Start Date</Label>
                <Input
                  type="date"
                  value={timeOffForm.start_date}
                  onChange={(e) => setTimeOffForm({ ...timeOffForm, start_date: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>End Date</Label>
                <Input
                  type="date"
                  value={timeOffForm.end_date}
                  onChange={(e) => setTimeOffForm({ ...timeOffForm, end_date: e.target.value })}
                  required
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Reason</Label>
              <Select
                value={timeOffForm.reason}
                onValueChange={(v) => setTimeOffForm({ ...timeOffForm, reason: v })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select reason..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Vacation">Vacation</SelectItem>
                  <SelectItem value="Sick">Sick Leave</SelectItem>
                  <SelectItem value="Personal">Personal Day</SelectItem>
                  <SelectItem value="Family">Family Emergency</SelectItem>
                  <SelectItem value="Other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Additional Notes (Optional)</Label>
              <Textarea
                value={timeOffForm.notes}
                onChange={(e) => setTimeOffForm({ ...timeOffForm, notes: e.target.value })}
                placeholder="Any additional details..."
                rows={3}
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setTimeOffDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={saving}>
                {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Submit Request
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Time Entry Dialog */}
      <Dialog open={timeEntryDialogOpen} onOpenChange={setTimeEntryDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Log Time Entry</DialogTitle>
            <DialogDescription>Record your work hours</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmitTimeEntry} className="space-y-4">
            <div className="space-y-2">
              <Label>Date</Label>
              <Input
                type="date"
                value={timeEntryForm.date}
                onChange={(e) => setTimeEntryForm({ ...timeEntryForm, date: e.target.value })}
                required
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Hours</Label>
                <Input
                  type="number"
                  step="0.5"
                  min="0"
                  max="24"
                  value={timeEntryForm.hours}
                  onChange={(e) => setTimeEntryForm({ ...timeEntryForm, hours: e.target.value })}
                  placeholder="e.g., 8"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Type</Label>
                <Select
                  value={timeEntryForm.type}
                  onValueChange={(v) => setTimeEntryForm({ ...timeEntryForm, type: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="regular">Regular</SelectItem>
                    <SelectItem value="overtime">Overtime</SelectItem>
                    <SelectItem value="highrise">Highrise</SelectItem>
                    <SelectItem value="lowrise">Lowrise</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Notes (Optional)</Label>
              <Textarea
                value={timeEntryForm.notes}
                onChange={(e) => setTimeEntryForm({ ...timeEntryForm, notes: e.target.value })}
                placeholder="Job details, location, etc."
                rows={3}
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setTimeEntryDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={saving}>
                {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Submit
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Settings Dialog */}
      <Dialog open={settingsDialogOpen} onOpenChange={setSettingsDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Preferences</DialogTitle>
            <DialogDescription>Customize your portal experience</DialogDescription>
          </DialogHeader>
          <div className="space-y-6">
            <div className="space-y-2">
              <Label>Theme</Label>
              <div className="flex gap-2">
                <Button
                  variant={theme === "light" ? "default" : "outline"}
                  onClick={() => setTheme("light")}
                  className="flex-1"
                >
                  <Sun className="h-4 w-4 mr-2" />
                  Light
                </Button>
                <Button
                  variant={theme === "dark" ? "default" : "outline"}
                  onClick={() => setTheme("dark")}
                  className="flex-1"
                >
                  <Moon className="h-4 w-4 mr-2" />
                  Dark
                </Button>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Accent Color</Label>
              <div className="flex gap-2 items-center">
                <Input
                  type="color"
                  value={userPreferences.accent_color}
                  onChange={(e) =>
                    savePreferences({ ...userPreferences, accent_color: e.target.value })
                  }
                  className="w-16 h-10 p-1"
                />
                <div className="flex gap-1">
                  {["#10b981", "#6366f1", "#f59e0b", "#ef4444", "#8b5cf6", "#06b6d4"].map(
                    (color) => (
                      <button
                        key={color}
                        className="w-8 h-8 rounded-full border-2 transition-all hover:scale-110"
                        style={{
                          backgroundColor: color,
                          borderColor:
                            userPreferences.accent_color === color ? "white" : color,
                        }}
                        onClick={() =>
                          savePreferences({ ...userPreferences, accent_color: color })
                        }
                      />
                    )
                  )}
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={() => setSettingsDialogOpen(false)}>Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EmployeeDashboard;
